// Copyright (C) Microsoft Corporation. All rights reserved.
import type { ExtensionPrompt, Group, Prompt, UserPrompt } from "@1js/pl-types";
export type {
  ExtensionPrompt,
  Group,
  HydratedEntity,
  MicrosoftPrompt,
  Prompt,
  PromptCategory,
  PromptRingType,
  UserPrompt,
} from "@1js/pl-types";

export type Appearance =
  | "in-app"
  | "web"
  | "m365"
  | "win-copilot"
  | "single-col"
  | "bing-mobile" // deprecated, it now just uses same style as 'single-col'. Remove this once <PromptLibraryDialog> from BizChat is using the now more simplified appearance.
  | "bing-web" // deprecated, it now just uses same style as 'm365'. Remove this once <PromptLibraryDialog> from BizChat is using the now more simplified appearance.
  | "underside"; // deprecated, it now just uses same style as 'single-col'. Remove this once <PromptLibraryDialog> from BizChat is using the now more simplified appearance.

export const ProductTypes = [
  "Word",
  "PowerPoint",
  "Outlook",
  "Teams",
  "BizChat",
  "Copilot",
  "Excel",
  "OneNote",
  "OneDrive",
  "Loop",
  "Whiteboard",
  "SharePoint",
  "Topics",
  "Forms",
  "Planner",
  "Stream",
  "VivaEngage",
  "VivaGoals",
  "AzureWeb",
  "Azure",
  "CWC",
] as const;

export type ProductType = (typeof ProductTypes)[number];

export const JobTypes = ["Department", "Industry", "Education"] as const;
export type JobType = (typeof JobTypes)[number];

export type Product = {
  name: ProductType;
  surface?: string[];
};

export type PromptStatistics = {
  sharedCount: number;
  savedCount: number;
};

export type HeroData = {
  enabled: boolean;
  rank: number;
  altText: string;
  image: string;
};

export type PromptTier = "Work" | "Personal";

export type FacetDictionary = {
  [key: string]: FacetValue[];
};

export type FacetValue = {
  /**
   * Displayed value.
   */
  value: string;

  count?: number;

  /**
   * Internal Id for mapping.
   */
  id?: string;
};

// A custom weighting function on product types so we can sort product type in a certain order.
export const ProductWeights: Record<ProductType, number> = {
  BizChat: 1,
  Teams: 2,
  Outlook: 3,
  Word: 4,
  Excel: 5,
  PowerPoint: 6,
  OneNote: 7,
  Loop: 8,
  Whiteboard: 9,
  Planner: 10,
  Stream: 11,
  Forms: 12,
  VivaEngage: 13,
  VivaGoals: 13,
  Copilot: 13,
  OneDrive: 13,
  SharePoint: 13,
  Topics: 13,
  Azure: 13,
  AzureWeb: 13,
  CWC: 13,
};

export const JobTypeWeights: Record<JobType, number> = {
  Department: 1,
  Industry: 2,
  Education: 3,
};

export const MockExtensionPrompts: ExtensionPrompt[] = [
  {
    Id: "3p_jiraPlugin_KaOKxlNqymOQ6QxtSrNRTyO/Ynq+yDgI5/RImGOsEaM=_en-us",
    CommandText: "List my Jira projects",
    DisplayDescription: "List my Jira projects",
    Title: "",
    DisplayCategory: "Ask",
    DisplayText: "List my Jira projects",
    PromptId: "jiraPlugin_KaOKxlNqymOQ6QxtSrNRTyO/Ynq+yDgI5/RImGOsEaM=",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
      },
      Execution: {},
    },
    Origin: "Plugin",
    ProviderId: "U_ccd08fa5-2a37-fe30-3808-23267ca240de",
    ProviderName: "Jira Cloud",
    ProviderPartyType: "ThirdParty",
    Text: "List my Jira projects",
    ReferenceId: "31237a22-8666-8b85-a455-c4a1fb05d5e2.2000.3",
    Products: ["BizChat"],
  },
  {
    Id: "3p_jiraPlugin_CHPcWU4U3pYFDq/U4FLX2b7e2lP7Ll5rPKRUTLWm3f0=_en-us",
    CommandText: "Add comments to a Jira issue",
    DisplayDescription: "Add comments to a Jira issue",
    Title: "",
    DisplayCategory: "Ask",
    DisplayText: "Add comments to a Jira issue",
    PromptId: "jiraPlugin_CHPcWU4U3pYFDq/U4FLX2b7e2lP7Ll5rPKRUTLWm3f0=",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
      },
      Execution: {},
    },
    Origin: "Plugin",
    ProviderId: "U_ccd08fa5-2a37-fe30-3808-23267ca240de",
    ProviderName: "Jira Cloud",
    ProviderPartyType: "ThirdParty",
    Text: "Add comments to a Jira issue",
    ReferenceId: "31237a22-8666-8b85-a455-c4a1fb05d5e2.2000.4",
    Products: ["BizChat"],
  },
];

export const MockPrompts: Prompt[] = [
  {
    Id: "b66be088-70ff-4b75-b8ad-21614941aae0_en-us",
    PromptId: "b66be088-70ff-4b75-b8ad-21614941aae0",
    Title: "Share meeting notes",
    DisplayText:
      "Draft an email with notes and action items from <entity type='meeting'>meeting</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Draft an email with notes and action items from <entity type='meeting'>meeting</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "BizChatPri", Value: "1" }],
  },
  {
    Id: "95024772-2835-4ed3-991f-e1bc040a7b30_en-us",
    PromptId: "95024772-2835-4ed3-991f-e1bc040a7b30",
    Title: "Write a status update",
    DisplayText:
      "Write a status report to my manager based on <entity type='file'>file</entity>. Include any upcoming deliverables, due dates, and key decisions",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText:
      "Write a status report to my manager based on <entity type='file'>file</entity>. Include any upcoming deliverables, due dates, and key decisions.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Financial Services" }],
  },
  {
    Id: "9234a086-5ac9-4cc8-8a47-27b8b7631b67_en-us",
    PromptId: "9234a086-5ac9-4cc8-8a47-27b8b7631b67",
    Title: "Catch up on messages",
    DisplayText: "Summarize Teams messages from this week about...",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText: "Summarize Teams messages from this week about...",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Financial Services" }],
  },
  {
    Id: "e34b9f77-2653-4264-a133-6582cb97c1a1_en-us",
    PromptId: "e34b9f77-2653-4264-a133-6582cb97c1a1",
    Title: "How to",
    DisplayText: "How do I write a request for proposal?",
    DisplayDescription: "",
    DisplayCategory: "Ask",
    CommandText: "How do I write a request for proposal?",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Retail" }],
  },
  {
    Id: "7d93e1a8-5813-4c8e-abf9-92adf09564b2_en-us",
    PromptId: "7d93e1a8-5813-4c8e-abf9-92adf09564b2",
    Title: "Help me write",
    DisplayText:
      "Write an email to my team about our top priorities for next quarter from <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Write an email to my team about our top priorities for next quarter from <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Manufacturing" }],
  },
  {
    Id: "892b000a-6c7b-4399-8792-c96a949d2cca_en-us",
    PromptId: "892b000a-6c7b-4399-8792-c96a949d2cca",
    Title: "Discover a company topic",
    DisplayText:
      "I need to explain [concept] to a [role/target audience]. I need to include an explainer of what it is, how it works, and a",
    DisplayDescription:
      "\\u003Cb\\u003EExample\\u003C/b\\u003E\\u003Cbr\\u003E\\u0026quot;I need to explain  \\u003Cb\\u003EExpense Management\\u003C/b\\u003E to a \\u003Cb\\u003Enew accountant intern\\u003C/b\\u003E, including an explanation of what it is, how it works, and the value proposition. Create a script that includes two analogies to help explain it to someone new to the concept.\\u0026quot;\\u003Cbr\\u003E\\u003Cbr\\u003E",
    DisplayCategory: "Create",
    CommandText:
      "I need to explain [concept] to a [role/target audience]. I need to include an explainer of what it is, how it works, and a value proposition associated with it. Create a script I could use. Include 2 analogies to help it make sense to someone new to this concept.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Manufacturing" }],
  },
  {
    Id: "56eb2c14-2988-45fe-aa24-9b90d24328d8_en-us",
    PromptId: "56eb2c14-2988-45fe-aa24-9b90d24328d8",
    Title: "Draft an FAQ",
    DisplayText: "Create an FAQ based on <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText: "Create an FAQ based on <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Manufacturing" }],
  },
  {
    Id: "674f6995-90f4-4ff8-b127-b3dd754a68b6_en-us",
    PromptId: "674f6995-90f4-4ff8-b127-b3dd754a68b6",
    Title: "Get calendar info",
    DisplayText:
      "When's my next meeting with <entity type='person'>person</entity>?",
    DisplayDescription: "",
    DisplayCategory: "Ask",
    CommandText:
      "When's my next meeting with <entity type='person'>person</entity>?",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Human Resources" }],
  },
  {
    Id: "94ffeade-968d-4009-b9ed-509ae8fc9154_en-us",
    PromptId: "94ffeade-968d-4009-b9ed-509ae8fc9154",
    Title: "Create a quiz ",
    DisplayText:
      "Write a practice quiz about [Topic]. Include at least 20 questions, and group questions by category. Provide a detailed",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Write a practice quiz about [Topic]. Include at least 20 questions, and group questions by category. Provide a detailed answer for each question.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Human Resources" }],
  },
  {
    Id: "aff7c8b9-89e6-4026-8da4-b5a1f4e736c6_en-us",
    PromptId: "aff7c8b9-89e6-4026-8da4-b5a1f4e736c6",
    Title: "Create course list",
    DisplayText:
      "Create 5 short courses for business leaders based on <placeholder>upload your file</placeholder>.",
    DisplayDescription:
      "Create 5 short courses for business leaders based on <placeholder>upload your file</placeholder>.",
    DisplayCategory: "Create",
    CommandText:
      "Create 5 short courses for business leaders based on <placeholder>upload your file</placeholder>.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Marketing" }],
  },
  {
    Id: "6f055863-e85d-4638-9086-a79eea53908f_en-us",
    PromptId: "6f055863-e85d-4638-9086-a79eea53908f",
    Title: "Prep for a meeting",
    DisplayText:
      "I've got an upcoming meeting with <entity type='person'>person<entity>. Based on my recent communication and recent news items, create a bulleted list.<placeholder></placeholder>",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText:
      "I've got an upcoming meeting with <entity type='person'>person<entity>. Based on my recent communication and recent news items, create a bulleted list of things to know ahead of this meeting.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Sad PM" }],
  },
  {
    Id: "6bf6bdda-d865-41e7-ab15-efb9bc0645db_en-us",
    PromptId: "6bf6bdda-d865-41e7-ab15-efb9bc0645db",
    Title: "Get key info",
    DisplayText: "List key points from <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText: "List key points from <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "0101b4e6-15b5-464b-abfa-d2dc648bf0b5_en-us",
    PromptId: "0101b4e6-15b5-464b-abfa-d2dc648bf0b5",
    Title: "Brainstorm",
    DisplayText:
      "Suggest 10 compelling taglines based on <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Suggest 10 compelling taglines based on <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "eb65855b-5eaf-4184-8a54-c2301e023ecd_en-us",
    PromptId: "eb65855b-5eaf-4184-8a54-c2301e023ecd",
    Title: "Draft an email template",
    DisplayText:
      "Draft a personal email I can send to people in [role] to call attention to recent [Product] news. Use <entity type='meeting'>meeting</entity> to add context.",
    DisplayDescription:
      "\\u003Cb\\u003EExample\\u003C/b\\u003E\\u003Cbr\\u003E\\u0026quot;Draft a personal email I can send to people in \\u003Cb\\u003ESales\\u003C/b\\u003E to call attention to recent \\u003Cb\\u003Eelectric bike\\u003C/b\\u003E news. Use \\u003Cu\\u003EWeekly_product_review\\u003C/u\\u003E to add context.\\u0026quot;\\u003Cbr\\u003E\\u003Cbr\\u003E",
    DisplayCategory: "Create",
    CommandText:
      "Draft a personal email I can send to people in [role] to call attention to recent [Product] news. Use <entity type='meeting'>meeting</entity> to add context.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "0110c811-9f0d-486d-b884-ef265111218e_en-us",
    PromptId: "0110c811-9f0d-486d-b884-ef265111218e",
    Title: "Recently asked questions",
    DisplayText:
      "Look for emails about [topic] over the last two weeks. Find common themes and organize them into a list.",
    DisplayDescription:
      "\\u003Cb\\u003EExample\\u003C/b\\u003E\\u003Cbr\\u003E\\u0026quot;Look for emails about \\u003Cb\\u003Eour 2024 company growth strategy\\u003C/b\\u003E over the last two weeks. Find common themes and organize them into a list.\\u0026quot;\\u003Cbr\\u003E\\u003Cbr\\u003E",
    DisplayCategory: "Understand",
    CommandText:
      "Look for emails about [topic] over the last two weeks. Find common themes and organize them into a list.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "96359aba-ce76-4496-afcb-60ac13ad3b78_en-us",
    PromptId: "96359aba-ce76-4496-afcb-60ac13ad3b78",
    Title: "Code a binary search in Python",
    DisplayText: "Write a Python script to perform binary search",
    DisplayDescription: "",
    DisplayCategory: "Code",
    CommandText: "Write a Python script to perform binary search",
    Products: ["CWC"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "BizChatPri", Value: "1" }],
  },
];

export const MockSavedPrompts: UserPrompt[] = [
  // User authored from Production site, edited user details
  {
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Id: "4788d584-1eaa-4a18-8f9c-459950cfcf01",
    CommandText: "How to write a proposal?",
    Title: "Saved prompt",
    DisplayCategory: "Created by Me",
    DisplayText: "How to write a proposal?",
    Products: ["BizChat"],
    PromptId: "4788d584-1eaa-4a18-8f9c-459950cfcf01",
    Author: {
      DisplayName: "Daniela Mandera",
      Email: "daniela@contoso.com",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
    LogicalId: "5c97425e-d593-4fec-ae96-31c82579e874",
    ReferenceId: null,
    Origin: "User",
    CreatedDateTimeInUtc: "2024-03-01T18:22:03+00:00",
    LastUpdatedDateTimeInUtc: "2024-03-01T18:22:04+00:00",
  },
  {
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Id: "6788d584-1eaa-4a18-8f9c-459950cfcf01",
    CommandText: "What should I prepare for a design review?",
    Title: "Be prepared",
    DisplayCategory: "Created by Me",
    DisplayText: "What should I prepare for a design review?",
    Products: ["BizChat"],
    PromptId: "6788d584-1eaa-4a18-8f9c-459950cfcf01",
    Author: {
      DisplayName: "Daniela Mandera",
      Email: "daniela@contoso.com",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
    LogicalId: "6c97425e-d593-4fec-ae96-31c82579e874",
    ReferenceId: null,
    Origin: "User",
    CreatedDateTimeInUtc: "2024-04-01T18:22:03+00:00",
    LastUpdatedDateTimeInUtc: "2024-04-01T18:22:04+00:00",
  },
  // Microsoft authored from Production site, edited user details
  {
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Id: "e3965690-49af-462c-b308-3d603275e2a9",
    CommandText:
      "What emails should be on my radar? Focus on those that seem urgent, important or are from my manager.",
    Title: "Microsoft authored",
    DisplayCategory: "Created by Me",
    DisplayText:
      "What emails should be on my radar? Focus on those that seem urgent, important or are from my manager.",
    Products: ["BizChat"],
    PromptId: "e3965690-49af-462c-b308-3d603275e2a9",
    Author: {
      DisplayName: "Daniela Mandera",
      Email: "daniela@contoso.com",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
    LogicalId: "7b1b3d1d-7ba4-44f8-89c2-186eb1ad16bf",
    ReferenceId: null,
    Origin: "User",
    CreatedDateTimeInUtc: "2024-04-19T07:37:25+00:00",
    LastUpdatedDateTimeInUtc: "2024-04-19T07:37:25+00:00",
  },
];

export const MockMemberGroups: Group[] = [
  {
    Id: "123",
    DisplayName: "Some group name 1",
    IsMember: true,
  },
  {
    Id: "1234",
    DisplayName: "Some group name 2",
    IsMember: true,
  },
  {
    Id: "123432",
    DisplayName: "Some group name 4",
    IsMember: true,
  },
];

export const MockHasPromptsGroups: Group[] = [
  {
    Id: "123",
    DisplayName: "Some group name 1",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "1234",
    DisplayName: "Some group name 2",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "123456",
    DisplayName: "Some group name 3",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "123432",
    DisplayName: "Some group name 4",
    HasPrompts: true,
    IsMember: true,
  },
];

export const MockGroups: Group[] = [
  {
    Id: "123",
    DisplayName: "Some group name 1",
    IsMember: true,
  },
  {
    Id: "1234",
    DisplayName: "Some group name 2",
    IsMember: true,
  },
  {
    Id: "123456",
    DisplayName: "Some group name 3",
    IsMember: true,
  },
  {
    Id: "123432",
    DisplayName: "Some group name 4",
    IsMember: true,
  },
];

const group1Prompts: Prompt[] = [
  {
    Slug: "suggest-new-content-a9008137-13c4-4131-9ccb-d1d13b28524c",

    Id: "a9008137-13c4-4131-9ccb-d1d13b28524c_en-us",
    PromptId: "a9008137-13c4-4131-9ccb-d1d13b28524c",
    Title: "Suggest new content",
    DisplayText:
      "Write about the benefits of <placeholder>focused work</placeholder>",
    DisplayDescription:
      "<b>Take it to the next level</b><br>Give Copilot more context about how you want to use the document. For example: Write an article for our company newsletter about the benefits of focused work. Keep the tone informal, and include examples whenever possible.<br><br><b>More to try</b><br>If the draft Copilot creates isn\\u0027t quite long enough, you can follow up by asking it to write more about the topic.",
    DisplayCategory: "Create",
    CommandText: "Write about the benefits of",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write about the benefits of <placeholder>focused work</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Slug: "write-cover-letter-4a90f90d-1805-4f5b-8091-b0215dbb4b35",

    Id: "4a90f90d-1805-4f5b-8091-b0215dbb4b35_en-us",
    PromptId: "4a90f90d-1805-4f5b-8091-b0215dbb4b35",
    Title: "Write cover letter",
    DisplayText:
      "Write a cover letter for <placeholder>a tour guide job</placeholder>.",
    DisplayDescription:
      "<b>Take it to the next level</b><br>The more details you can give it, the more accurate the draft will be.",
    DisplayCategory: "Create",
    CommandText: "Write a cover letter for",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a cover letter for <placeholder>a tour guide job</placeholder>.",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
];

const group2Prompts: Prompt[] = [
  {
    Slug: "write-a-marketing-plan-e52cd2bc-0fb3-4233-a534-e8368b5f5b63",

    Id: "e52cd2bc-0fb3-4233-a534-e8368b5f5b63_en-us",
    PromptId: "e52cd2bc-0fb3-4233-a534-e8368b5f5b63",
    Title: "Write a marketing plan",
    DisplayText:
      "Write a 1 page marketing plan for <placeholder>a coffee shop</placeholder>.",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText: "Write a 1 page marketing plan for",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a 1 page marketing plan for <placeholder>a coffee shop</placeholder>.",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Slug: "write-a-proposal-810dae88-7d9b-422a-8f24-a3bb07f71c49",

    Id: "810dae88-7d9b-422a-8f24-a3bb07f71c49_en-us",
    PromptId: "810dae88-7d9b-422a-8f24-a3bb07f71c49",
    Title: "Write a proposal",
    DisplayText:
      "Write a project proposal to <placeholder>convince my manager to allocate resources to a new initiative</placeholder>",
    DisplayDescription:
      "<b>Take it to the next level</b><br>Add as many additional details as you can about the project to help Copilot be more specific and accurate.",
    DisplayCategory: "Create",
    CommandText: "Write a project proposal to",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a project proposal to <placeholder>convince my manager to allocate resources to a new initiative</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Slug: "draft-a-white-paper-619235ab-2c07-4412-8b77-717bda8a3bf1",

    Id: "619235ab-2c07-4412-8b77-717bda8a3bf1_en-us",
    PromptId: "619235ab-2c07-4412-8b77-717bda8a3bf1",
    Title: "Draft a white paper",
    DisplayText:
      "Write a short white paper about <placeholder>design thinking</placeholder>.",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText: "Write a short white paper about",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: true,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a short white paper about <placeholder>design thinking</placeholder>.",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
];

const group3Prompts: Prompt[] = [
  {
    Slug: "highlight-mentions-e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",

    Id: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1_en-us",
    PromptId: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",
    Title: "Highlight mentions",
    DisplayText:
      "Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    DisplayDescription: "",
    DisplayCategory: "Edit",
    CommandText: "Highlight in yellow all mentions of",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Slug: "summarize-f6f399bc-db16-40ab-986b-5fa9fe239f0f",

    Id: "f6f399bc-db16-40ab-986b-5fa9fe239f0f_en-us",
    PromptId: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    Title: "Summarize",
    DisplayText: "Summarize as a <placeholder>status email</placeholder>",
    DisplayDescription:
      "<b>To be clear</b><br>If you\\u2019d like to narrow the scope of what OneNote summarizes just select the text you want summarized before running the prompt.<br><br><b>Take it to the next level</b><br>You can give Copilot more detail about how you want the status email. For example consider adding 'Use a professional tone.'",
    DisplayCategory: "Understand",
    CommandText: "Summarize as a",
    Products: ["OneNote"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Summarize as a <placeholder>status email</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
];
const group4Prompts: Prompt[] = [
  // Microsoft authored prompt
  {
    Slug: "highlight-mentions-e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",

    Id: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1_en-us",
    PromptId: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",
    Title: "Highlight mentions",
    DisplayText:
      "Microsoft prompt Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    DisplayDescription: "",
    DisplayCategory: "Edit",
    CommandText: "Highlight in yellow all mentions of",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
    Origin: "Microsoft",
  },
  // Synthetic user prompt without user details
  {
    Id: "f6f399bc-db16-40ab-986b-5fa9fe239f0f_en-us",
    PromptId: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    Title: "Summarize",
    DisplayText:
      "User prompt without user. Summarize as a <placeholder>status email</placeholder>",
    DisplayDescription:
      "<b>To be clear</b><br>If you\\u2019d like to narrow the scope of what OneNote summarizes just select the text you want summarized before running the prompt.<br><br><b>Take it to the next level</b><br>You can give Copilot more detail about how you want the status email. For example consider adding 'Use a professional tone.'",
    DisplayCategory: "Understand",
    CommandText: "Summarize as a",
    Products: ["OneNote"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    ReferenceId: "00000000-0000-0000-0000-000000000000",
    Origin: "User",
  },
  // Synthetic user prompt with user details
  {
    Id: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    PromptId: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    Title: "Summarize",
    DisplayText:
      "User prompt with user. Summarize as a <placeholder>status email</placeholder>",
    DisplayDescription:
      "<b>To be clear</b><br>If you\\u2019d like to narrow the scope of what OneNote summarizes just select the text you want summarized before running the prompt.<br><br><b>Take it to the next level</b><br>You can give Copilot more detail about how you want the status email. For example consider adding 'Use a professional tone.'",
    DisplayCategory: "Understand",
    CommandText: "Summarize as a",
    Products: ["OneNote"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    ReferenceId: "00000000-0000-0000-0000-000000000000",
    Origin: "User",
    Author: {
      DisplayName: "Daniela Mandera",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
  },
];

export const MockGroupPrompts: Record<string, Prompt[]> = {};
MockGroupPrompts[MockGroups[0].Id] = group1Prompts;
MockGroupPrompts[MockGroups[1].Id] = group2Prompts;
MockGroupPrompts[MockGroups[2].Id] = group3Prompts;
MockGroupPrompts[MockGroups[3].Id] = group4Prompts;
